'use client';

import React, { Component, ErrorInfo, ReactNode } from 'react';

interface ErrorBoundaryProps {
  children: ReactNode;
  name: string;
  disableFallback?: boolean;
  fallback?: React.ReactElement;
  rescue?(): void;
}

interface ErrorBoundaryState {
  error: Error | null;
}

interface ExtendedError extends Error {
  request?: string;
  type?: string;
  code?: string;
}

const EXCLUDED_ERROR_NAMES = ['ChunkLoadError'];
const EXCLUDED_ERROR_CODES = ['CSS_CHUNK_LOAD_FAILED'];
const EXCLUDED_ERROR_MESSAGES = ['The object can not be found here.'];
const EXCLUDED_ERROR_MESSAGE_PATTERNS = [
  /^Failed to load chunk .+ from module \d+$/,
  /^Initiator \(\[Video \d+\]\) is not a Screen or a ScreenObject\./,
  /Failed to execute 'insertBefore' on 'Node'/,
  /Failed to execute 'removeChild' on 'Node'/,
  /Node\.insertBefore: Child to insert before is not a child of this node/,
  /Node\.removeChild: The node to be removed is not a child of this node/,
  /^Oe\[_t\]\.getAttribute is not a function$/,
  /\[Ad Render Failure\] No selector found/,
  /^can't access property/,
];

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    if (this.isExcludedError(error)) {
      this.props.rescue?.();
      return;
    }

    if (process.env.NODE_ENV !== 'production') {
      console.error('ERRORED at KEY:', this.props.name, error.message, errorInfo.componentStack);
    } else {
      console.error(`ERROR BOUNDARY [${this.props.name}]:`, error.message);
      this.logErrorToCoralogix(error, errorInfo);
    }

    this.props.rescue?.();
  }

  logErrorToCoralogix(error: Error, errorInfo: ErrorInfo): void {
    import('@coralogix/browser').then(module => {
      if (!this.isExcludedError(error)) {
        const extendedError = error as ExtendedError;
        module.CoralogixRum.error(`ERROR BOUNDARY: ${this.props.name}`, {
          code: extendedError.code,
          componentStack: errorInfo.componentStack,
          message: error.message,
          name: error.name,
        });
      }
    });
  }

  isExcludedError(error: unknown): boolean {
    if (!error) return false;

    const err = error as ExtendedError;

    return !!(
      (err.name && EXCLUDED_ERROR_NAMES.includes(err.name)) ||
      (err.code && EXCLUDED_ERROR_CODES.includes(err.code)) ||
      (err.message && EXCLUDED_ERROR_MESSAGES.includes(err.message)) ||
      (err.message && EXCLUDED_ERROR_MESSAGE_PATTERNS.some(pattern => pattern.test(err.message)))
    );
  }

  render(): ReactNode {
    const { children, disableFallback, fallback } = this.props;

    if (this.state.error) {
      //console.error('ERRORED at KEY:', name, this.state.error.toString());
      return disableFallback ? children : fallback || <h2>Something went wrong.</h2>;
    }

    return children;
  }
}
